import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { ApiClientService, PermissionService, ToastService, RightPanelService } from '@services/index';
import { TablePreferencesService } from '@services/local-storage/table-preferences.service';
import { Align, Permission } from 'app/enums';
import { SortType } from 'app/types';
import { FirmwareVersion } from 'app/types/firmware-version.type';
import { Filter, FilterForm } from './firmware-list.model';
import { BehaviorSubject, EMPTY, finalize, map, Observable, of, switchMap, take, tap } from 'rxjs';
import { GetFirmwareVersionsResponse } from '@services/api-client/responses';
import { sortStrings } from '@helpers/sort-string-helper';
import { ConfirmModalComponent } from '@shared/components/confirm-modal/confirm-modal.component';
import { ModalHelper } from '@helpers/modal-helper';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FirmwareDetailPanelComponent } from '../../components/firmware-detail-panel/firmware-detail-panel.component';
import { filter } from 'rxjs/operators';

@UntilDestroy()
@Component({
    selector: 'app-firmware-list',
    templateUrl: './firmware-list.component.html',
    styleUrl: './firmware-list.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FirmwareListComponent implements OnInit {
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly apiClientService = inject(ApiClientService);
    private readonly translateService = inject(TranslateService);
    private readonly modalService = inject(NgbModal);
    private readonly toastService = inject(ToastService);
    private readonly permissionService = inject(PermissionService);
    private readonly rightPanelService = inject(RightPanelService);
    private readonly tablePreferencesService = inject(TablePreferencesService);

    private static readonly tableId = 'firmware-list' as const;

    public readonly align = Align;
    public sortKey: keyof FirmwareVersion = 'releaseDate';
    public sortAscending = false;
    public loading = false;
    public filterForm: FormGroup<FilterForm>;
    private readonly defaultFilter: Filter = {
        searchPhrase: ''
    };
    public loadingFirmwareIds: string[] = [];
    public permissions = Permission;
    public firmwarePermissions: Permission[] = this.permissionService.userPermissions;
    public readonly canManageFirmware = this.firmwarePermissions.includes(Permission.WriteFirmware);
    private readonly updateSubject: BehaviorSubject<void> = new BehaviorSubject<void>(undefined);
    public update$: Observable<void> = this.updateSubject.asObservable();
    private readonly searchSubject: BehaviorSubject<void> = new BehaviorSubject<void>(undefined);
    public search$: Observable<void> = this.searchSubject.asObservable();
    private readonly sortSubject: BehaviorSubject<SortType<FirmwareVersion>> = new BehaviorSubject({
        key: this.sortKey,
        ascending: this.sortAscending
    });
    public sort$: Observable<SortType<FirmwareVersion>> = this.sortSubject.asObservable();
    public firmwareVersions$: Observable<GetFirmwareVersionsResponse>;
    private readonly deletedFirmwareIdsSubject: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);
    public deletedFirmwareIds$: Observable<string[]> = this.deletedFirmwareIdsSubject.asObservable();

    public get f() {
        return this.filterForm.controls;
    }

    public ngOnInit() {
        const saved = this.tablePreferencesService.getPreferences(FirmwareListComponent.tableId)?.sort;
        if (saved) {
            this.sortKey = saved.key as keyof FirmwareVersion;
            this.sortAscending = saved.ascending;
            this.sortSubject.next({ key: this.sortKey, ascending: this.sortAscending });
        }

        const fb = new FormBuilder();
        this.filterForm = fb.nonNullable.group(this.defaultFilter);
        this.firmwareVersions$ = this.update$.pipe(
            tap(() => {
                this.deletedFirmwareIdsSubject.next([]);
            }),
            switchMap(() =>
                this.apiClientService.getFirmwareVersions$().pipe(
                    switchMap((firmwareVersions: GetFirmwareVersionsResponse) =>
                        this.deletedFirmwareIds$.pipe(
                            map((deletedFirmwareIds) =>
                                firmwareVersions.filter((fw) => !deletedFirmwareIds.includes(fw.id))
                            )
                        )
                    ),
                    switchMap((firmwareVersions: GetFirmwareVersionsResponse) =>
                        this.search$.pipe(
                            map(() => {
                                return this.filterResponse(firmwareVersions);
                            })
                        )
                    ),
                    switchMap((firmwareVersions: GetFirmwareVersionsResponse) =>
                        this.sort$.pipe(
                            switchMap((sort) => {
                                this.sortKey = sort.key;
                                this.sortAscending = sort.ascending;
                                this.loading = false;
                                this.changeDetectorRef.markForCheck();

                                return of(sortStrings<FirmwareVersion>(firmwareVersions, sort.key, sort.ascending));
                            })
                        )
                    )
                )
            )
        );

        this.filterForm.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
            this.searchSubject.next();
        });
    }

    private filterResponse(firmwareVersions: FirmwareVersion[]): FirmwareVersion[] {
        const searchPhrase = this.f.searchPhrase.value.toLowerCase().trim();

        return firmwareVersions.filter((firmware) => {
            return (
                firmware.name.toLowerCase().includes(searchPhrase) ||
                firmware.version.toLowerCase().includes(searchPhrase)
            );
        });
    }

    public openFirmwareDetailPanel(firmware?: FirmwareVersion) {
        if (this.canManageFirmware) {
            const title: string = firmware
                ? (this.translateService.instant('MENU.FIRMWARE') as string) + ': ' + firmware.name
                : (this.translateService.instant('BUTTONS.CREATE_FIRMWARE') as string);

            this.rightPanelService.showComponent(
                title,
                FirmwareDetailPanelComponent,
                { firmwareId: firmware?.id },
                true,
                true,
                false,
                'system_update'
            );

            // Start in expanded mode for firmware detail
            this.rightPanelService.setExpanded(true);

            this.rightPanelService.isVisible$
                .pipe(
                    filter((visible: boolean) => !visible),
                    take(1),
                    untilDestroyed(this)
                )
                .subscribe(() => {
                    this.updateSubject.next();
                });
        }
    }

    public onSortChange(key: keyof FirmwareVersion, ascending: boolean) {
        this.tablePreferencesService.setPreferences(FirmwareListComponent.tableId, {
            sort: { key: key as string, ascending }
        });
        this.sortSubject.next({ key, ascending });
    }

    public confirmDeleteFirmware(event: Event, firmware: FirmwareVersion) {
        event.stopPropagation();
        if (this.loadingFirmwareIds.includes(firmware.id)) {
            return;
        }

        const modalRef = this.modalService.open(ConfirmModalComponent);
        const modalInstanceHelper = ModalHelper.getConfirmModalInstanceHelper(modalRef);

        modalInstanceHelper.componentInstance.model = {
            titleLanguageKey: 'FEATURES.FIRMWARE.TITLES.DELETE_FIRMWARE',
            bodyLanguageKey: 'FEATURES.FIRMWARE.TEXTS.CONFIRM_DELETE_FIRMWARE',
            cancelLanguageKey: 'BUTTONS.CANCEL',
            confirmLanguageKey: 'BUTTONS.CONFIRM'
        };

        modalInstanceHelper.confirmation$
            .pipe(
                untilDestroyed(this),
                switchMap((confirm) => {
                    if (confirm) {
                        this.loadingFirmwareIds.push(firmware.id);
                        this.changeDetectorRef.markForCheck();
                        return this.apiClientService.deleteFirmwareVersion(firmware.id);
                    }
                    return EMPTY;
                }),
                finalize(() => {
                    this.loadingFirmwareIds = this.loadingFirmwareIds.filter((id) => id !== firmware.id);
                    this.changeDetectorRef.markForCheck();
                })
            )
            .subscribe({
                next: () => {
                    const title = this.translateService.instant('TOAST_TITLES.SUCCESS') as string;
                    const message = this.translateService.instant(
                        'FEATURES.FIRMWARE.TOAST_MESSAGES.DELETE_FIRMWARE_SUCCESS'
                    ) as string;

                    this.toastService.open(title, message, { type: 'success', progressBar: true });
                    this.deletedFirmwareIdsSubject.next([...this.deletedFirmwareIdsSubject.value, firmware.id]);
                },
                error: () => {
                    const title = this.translateService.instant('TOAST_TITLES.ERROR') as string;
                    const message = this.translateService.instant(
                        'FEATURES.FIRMWARE.TOAST_MESSAGES.DELETE_FIRMWARE_ERROR'
                    ) as string;

                    this.toastService.open(title, message, { type: 'error', progressBar: true });
                }
            });
    }
}
